import { Box, FormControlLabel, TextField, Typography } from '@mui/material';
import React from 'react';

import { FieldRendererProps, fieldHasValue } from './InteractionFieldRenderers';
import { FormFieldItem, FormFieldType } from '../../../../@types';
import { Folders } from '../../../../@types/uploaded-files.type';
import { uploadFiles } from '../../../../api/uploadedFiles';
import { Checkbox } from '../../../../components/atoms/Checkbox';
import { DateTimePicker } from '../../../../components/atoms/DateTimePicker';
import { RadioGroup } from '../../../../components/atoms/RadioGroup';
import { SelectMenu } from '../../../../components/atoms/SelectMenu';
import { LabourTimeInput } from '../../../../components/molecules/LabourTimeInput';
import { UploadButton } from '../../../../components/molecules/UploadButton';
import { ServiceAttachmentUploadFeature } from '../../../../configs/enums';
import { SERVICE_ATTACHMENTS_MIME_TYPES } from '../../../../configs/envs';
import { getMatchingMimeTypes } from '../../../../utils/upload-file';

/**
 * One component per interaction field type, in the desktop visual language.
 *
 * Split out of `InteractionFieldRenderers` when supporting every field type pushed
 * that file past the 250-line limit — the dispatcher stays there, the presentation
 * lives here. Mirrors the mobile side, where `MobileInteractionPrimitiveFields` sits
 * beside `MobileInteractionFieldRenderer`.
 */

const UPLOAD_FAILED = 'Upload failed — please try again';

export const requiredErrorMessage = (field: FormFieldItem): string | undefined => {
  if (!field.isRequired) return undefined;
  return fieldHasValue(field) ? undefined : 'Required';
};

export const TextField_ = ({ field, onChange }: FieldRendererProps) => {
  const error = requiredErrorMessage(field);
  return (
    <TextField
      fullWidth
      size="small"
      multiline={field.type === FormFieldType.RichTextbox}
      minRows={field.type === FormFieldType.RichTextbox ? 2 : undefined}
      value={(field.value as string | undefined) ?? ''}
      onChange={(e) => onChange({ ...field, value: e.target.value })}
      error={!!error}
      helperText={error}
      slotProps={{ htmlInput: { 'aria-label': field.title } }}
    />
  );
};

export const DateField = ({ field, onChange }: FieldRendererProps) => (
  <DateTimePicker
    label={field.title}
    value={(field.value as string | undefined) ?? ''}
    onChange={(v) => onChange({ ...field, value: v })}
  />
);

export const LabourTimeField = ({ field, onChange }: FieldRendererProps) => {
  const error = requiredErrorMessage(field);
  return (
    <LabourTimeInput
      idPrefix={`interaction-field-${field.id}`}
      title={field.title || 'Labour Time'}
      value={typeof field.value === 'string' ? field.value : undefined}
      error={!!error}
      showRequiredError={!!error}
      onChange={(v) => onChange({ ...field, value: v })}
    />
  );
};

export const CheckboxField = ({ field, onChange }: FieldRendererProps) => (
  <FormControlLabel
    control={
      <Checkbox
        checked={!!field.value}
        onChange={(e) => onChange({ ...field, value: e.target.checked })}
      />
    }
    label={field.title}
    sx={{ ml: 0 }}
  />
);

export const DropdownField = ({ field, onChange }: FieldRendererProps) => {
  const items = (field.options ?? []).map((o) => ({ value: o.value, label: o.title }));
  return (
    <SelectMenu
      id={`interaction-field-${field.id}`}
      items={items}
      selectedValue={(field.value as string | undefined) ?? ''}
      onChange={(value) => onChange({ ...field, value })}
    />
  );
};

export const RadioField = ({ field, onChange }: FieldRendererProps) => (
  <RadioGroup
    id={`interaction-field-${field.id}`}
    title={field.title + (field.isRequired ? ' *' : '')}
    options={field.options ?? []}
    value={(field.value as string | undefined) ?? ''}
    error={!!requiredErrorMessage(field)}
    onChange={(value) => onChange({ ...field, value })}
  />
);

export const PresentationField = ({ field }: FieldRendererProps) => (
  <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{field.title}</Typography>
);

export interface UploadFieldProps extends FieldRendererProps {
  feature: ServiceAttachmentUploadFeature;
  mimeTypes: string;
}

export const UploadField = ({
  field,
  onChange,
  serviceId,
  feature,
  mimeTypes,
}: UploadFieldProps) => {
  // The failure message is local state, not the field's value. Writing it into the
  // value made `fieldHasValue` see a non-empty string, so a *required* attachment
  // passed validation after its upload had failed.
  const [uploadError, setUploadError] = React.useState<string>();
  const error = uploadError ?? requiredErrorMessage(field);

  if (!serviceId) {
    return (
      <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontStyle: 'italic' }}>
        Submit the form once to attach files — the service must exist before it can hold uploads.
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <UploadButton
        showName
        label={field.title}
        maxFileSize={1024}
        fileTypes={getMatchingMimeTypes(SERVICE_ATTACHMENTS_MIME_TYPES, mimeTypes)}
        value={field.value}
        setFileDetails={async (fileList: FileList) => {
          if (fileList.length === 0) return;
          const formData = new FormData();
          [...fileList].forEach((item) => formData.append('fileList', item));
          try {
            const result = await uploadFiles(
              serviceId,
              formData,
              Folders.ServiceAttachments,
              feature
            );
            const first = (result as Record<string, { fileName?: string; dbSuccess?: boolean }>)[
              '0'
            ];
            if (!first || !first.dbSuccess) {
              setUploadError(UPLOAD_FAILED);
              onChange({ ...field, value: '' });
              return;
            }
            setUploadError(undefined);
            onChange({ ...field, value: `${first.fileName} successfully uploaded` });
          } catch {
            setUploadError(UPLOAD_FAILED);
            onChange({ ...field, value: '' });
          }
        }}
      />
      {error && <Typography sx={{ fontSize: '11px', color: 'error.main' }}>{error}</Typography>}
    </Box>
  );
};

export const EtaField = ({ field, onChange }: FieldRendererProps) => (
  <DateTimePicker
    label={field.title || 'Estimated Time of Arrival'}
    value={(field.value as string | undefined) ?? ''}
    onChange={(v) => onChange({ ...field, value: v })}
  />
);

/** A parameter the backend sets from the template — read-only context, no input. */
export const AutoParameterField = ({ field }: FieldRendererProps) => (
  <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontStyle: 'italic' }}>
    {field.title}: Will be set automatically
  </Typography>
);

export const GetParameterField = ({ field }: FieldRendererProps) => {
  const preset = field.preSelectedData as { parameterValue?: string } | undefined;
  const shown = preset?.parameterValue || (field.value as string | undefined) || 'N/A';
  return <Typography sx={{ fontSize: '13px' }}>{`${field.title}: ${shown}`}</Typography>;
};

/**
 * Rendered when a field needs the whole service and the caller could not supply
 * it. Explicit beats a silently empty control.
 */
export const ServiceContextRequired = ({ field }: FieldRendererProps) => (
  <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
    {`${field.title}: Service context required`}
  </Typography>
);

// Defensive: the panel routes forms with non-natively-supported fields through
// the legacy FormInterceptor, so this branch should never render. If it does,
// surface a small breadcrumb instead of failing silently.
export const UnsupportedField = ({ field }: FieldRendererProps) => (
  <Typography sx={{ fontSize: '12px', fontStyle: 'italic', color: 'text.disabled' }}>
    {`"${field.type}" rendered without legacy fallback — this is a bug.`}
  </Typography>
);
